import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider } from "react-hook-form"
import { zodResolver } from '@hookform/resolvers/zod';
import { ConfigProvider, Steps, theme, Row, Col, message, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import MultiFormNavigation from '@/components/multiformNavigation';
import Responsavel from './Steps/responsavel';
import Student from './Steps/student';
import Disciplinas from './Steps/disciplinas';
import Produtos from './Steps/produtos';
import Pagamento from './Steps/pagamentos';
import ResumoPagamento from './ResumoPagamento';
import { MatriculaAlunoSchema, matriculaAlunoSchema } from '@/schema/matriculaAlunoSchema';
import { getAlunoById, postAluno } from '@/services/aluno';
import { errorHandler, mostrarErro } from '@/utils/errorhandler';
import _ from 'lodash';
import { useUpdateEffect } from '@/hooks/useUpdateEffect';
import { formatToApi, formatToBr, isInvalidDateInput, normalizeDataParcelaToApi } from '@/utils/dateUtils';
import cpfValidation from '@/utils/cpfValidation';
import { usePermissions } from '@/hooks/usePermissions';
import { calcularTotalMatricula, somarDisciplinas, somarParcelasVigentes } from '@/utils/totaisMatricula';

const steps = [
  'Responsável',
  'Aluno',
  'Específicas',
  'Produtos',
  'Pagamento',
];

/** Índice do step de Pagamento (último do wizard). */
const STEP_PAGAMENTO = 4;


function CadastroAluno() {
  const [current, setCurrent] = useState(0);
  const [alunoId, setAlunoId] = useState('');
  const { id } = useParams();
  const isEditable = id ? true : false;
  const { isMaster } = usePermissions();

  const navigate = useNavigate();

  const methods = useForm<MatriculaAlunoSchema>({
    resolver: zodResolver(matriculaAlunoSchema),
    defaultValues: {
      tipoMatricula: '1',
      estudante: {
        pessoaEstudante: {},
      },
    },
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = methods;

  // Aluno já persistido com status "Matriculado" (id 1): os steps de
  // Disciplinas e Pagamento abrem somente para consulta (campos/botões
  // desabilitados), mantendo apenas o Salvar Cadastro habilitado.
  // Exceção: o perfil MASTER altera a matrícula em qualquer status.
  const isMatriculado = isEditable && watch('statusMatriculaId') === 1 && !isMaster;

  const onSubmit = handleSubmit(async (data: MatriculaAlunoSchema) => {
    if (data.tipoMatricula === '1'
      && calcularTotalMatricula(data.disciplinas, data.produtos).toFixed(2)
      // Parcela trocada por outra forma de pagamento não conta: ela fica na
      // matrícula só como registro da troca.
      !== somarParcelasVigentes(data.pagamentos).toFixed(2)) {
      message.error('Valor de pagamento deve ser igual ao valor total das disciplinas e produtos selecionados!');
      return;
    }

    const dataNascResponsavel = data.responsavel?.pessoaResponsavel?.dataNascimento;
    if (isInvalidDateInput(dataNascResponsavel)) {
      message.error('Data de nascimento do responsável inválida. Use o formato DD/MM/AAAA.');
      return;
    }
    const dataNascEstudante = data.estudante?.pessoaEstudante?.dataNascimento;
    if (isInvalidDateInput(dataNascEstudante)) {
      message.error('Data de nascimento do aluno inválida. Use o formato DD/MM/AAAA.');
      return;
    }

    const cpfResponsavel = data.responsavel?.pessoaResponsavel?.cpfCnpj;
    if (cpfResponsavel && !cpfValidation(cpfResponsavel)) {
      message.error('CPF do responsável inválido.');
      return;
    }
    const cpfEstudante = data.estudante?.pessoaEstudante?.cpfCnpj;
    if (cpfEstudante && !cpfValidation(cpfEstudante)) {
      message.error('CPF do aluno inválido.');
      return;
    }

    if (data.estudante?.semestresEstudo == "" ) {
      data.estudante.semestresEstudo = undefined; // Limpa o campo para evitar enviar valor inválido
    }

    try {
      if (data.responsavel?.pessoaResponsavel) {
        const apiDate = formatToApi(dataNascResponsavel);
        data.responsavel.pessoaResponsavel.dataNascimento = apiDate ?? undefined;
      }
      if (data.estudante?.pessoaEstudante) {
        const apiDate = formatToApi(dataNascEstudante);
        data.estudante.pessoaEstudante.dataNascimento = apiDate ?? undefined;
      }
      data.pagamentos.forEach((pagamento) => {
        const dataParcelaNormalizada = normalizeDataParcelaToApi(pagamento.dataParcela);
        pagamento.dataParcela = dataParcelaNormalizada ?? pagamento.dataParcela;
      });
      data.statusMatriculaId = parseInt(data.tipoMatricula);
      const response = await postAluno(data);
      if (response.status >= 200 && response.status < 300) {
        message.success('Cadastro finalizado com sucesso!');
        return navigate("/alunos");
      }
      // Sem 2xx e sem exceção (ex.: 304): avisa em vez de não fazer nada.
      mostrarErro(`Não foi possível salvar o cadastro (código ${response.status}).`);
    } catch (error: any) {
      errorHandler(error);
    }
  });

  useEffect(() => {
    if (Object.keys(errors).length === 0) return;

    // Percorre recursivamente o objeto de erros do react-hook-form e
    // coleta { path, message } de cada folha que tenha mensagem.
    const collected: { path: string; message: string }[] = [];
    const visit = (node: any, path: string[]) => {
      if (!node || typeof node !== 'object') return;
      if (typeof node.message === 'string' && node.message.length > 0 && (node.type || node.ref || path.length > 0)) {
        collected.push({ path: path.join('.'), message: node.message });
        return;
      }
      if (Array.isArray(node)) {
        node.forEach((item, idx) => visit(item, [...path, String(idx)]));
        return;
      }
      Object.entries(node).forEach(([key, value]) => {
        if (key === 'ref' || key === 'type' || key === 'message' || key === 'types') return;
        visit(value, [...path, key]);
      });
    };
    visit(errors, []);

    // Deduplica mensagens preservando a ordem.
    const uniqueMessages = Array.from(new Set(collected.map((c) => c.message)));

    message.error({
      content: (
        <>
          Verifique os campos obrigatórios:
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {uniqueMessages.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </>
      ),
    });

    // Foca automaticamente o primeiro campo com erro.
    const firstPath = collected[0]?.path;
    if (firstPath) {
      try {
        methods.setFocus(firstPath as any);
      } catch {
        // setFocus pode falhar se o campo não estiver registrado/montado no step atual; ignoramos.
      }
    }
  }, [errors, methods]);

  useEffect(() => {
    if (id) {
      setAlunoId(id);
    }
  }, []);

  useUpdateEffect(() => {
    fetchAluno();
  }, [alunoId]);

  const fetchAluno = async () => {
    if (id) {
      let aluno: any;
      try {
        aluno = (await getAlunoById(id)).data;
      } catch (error: any) {
        // Sem isto a tela de alteração abria vazia, sem nenhum aviso.
        errorHandler(error);
        return;
      }

      // Mapear statusMatriculaId para tipoMatricula
      let tipoMatricula = '1'; // Default: Matrícula
      if (aluno.statusMatriculaId === 1) {
        tipoMatricula = '1'; // Matrícula
      } else if (aluno.statusMatriculaId === 4) {
        tipoMatricula = '4'; // Pré-Matrícula
      } else if (aluno.statusMatriculaId === 5) {
        tipoMatricula = '5'; // Orçamento
      }

      const alunoNormalizado = _.cloneDeep(aluno);
      // Normaliza datas de nascimento para DD/MM/YYYY (formato interno do form).
      if (alunoNormalizado?.responsavel?.pessoaResponsavel) {
        alunoNormalizado.responsavel.pessoaResponsavel.dataNascimento =
          formatToBr(alunoNormalizado.responsavel.pessoaResponsavel.dataNascimento) || undefined;
      }
      if (alunoNormalizado?.estudante?.pessoaEstudante) {
        alunoNormalizado.estudante.pessoaEstudante.dataNascimento =
          formatToBr(alunoNormalizado.estudante.pessoaEstudante.dataNascimento) || undefined;
      }

      reset({
        ...alunoNormalizado,
        tipoMatricula,
      });
    }
  }

  const validateStep = async () => {
    const fields = [
      'responsavel.nome',
      'responsavel.cpfCnpj',
      'responsavel.rg.numero',
      'responsavel.rg.emissor',
      'responsavel.rg.uf',
      'responsavel.email',
      'responsavel.endereco.cep',
      'responsavel.endereco.uf',
      'responsavel.endereco.cidade',
      'responsavel.endereco.bairro',
      'responsavel.endereco.estado',
      'responsavel.endereco.numero',
      'responsavel.profissao',
      'responsavel.grauParentesco',

    ];
    return methods.trigger(fields as [], { shouldFocus: true });
  }

  const next = async (saved: boolean) => {
    if (saved || current === steps.length - 1) {
      await onSubmit();
      return;
    }

    // Só avança do step de específicas (index 2) com alguma disciplina selecionada.
    if (current === 2) {
      const valorTotalDisciplinas = somarDisciplinas(watch('disciplinas'));

      if (valorTotalDisciplinas === 0) {
        message.warning('Selecione pelo menos uma disciplina antes de configurar os pagamentos');
        return;
      }
    }

    const output = await validateStep();

    if (!output) return

    setCurrent(current + 1);
  };

  const saveChange = async () => {
      await onSubmit();
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const onChange = (value: number) => {
    // Pagamento só abre depois de haver disciplinas selecionadas.
    if (value === STEP_PAGAMENTO) {
      const valorTotalDisciplinas = somarDisciplinas(watch('disciplinas'));

      if (valorTotalDisciplinas === 0) {
        message.warning('Selecione as disciplinas antes de configurar os pagamentos');
        return;
      }
    }
    setCurrent(value);
  }

  const mountSteps = () => {
    const valorTotalDisciplinas = somarDisciplinas(watch('disciplinas'));

    return steps.map((title, index) => ({
      title,
      disabled: index === STEP_PAGAMENTO && valorTotalDisciplinas === 0,
    }));
  };

  const renderForm = () => {
    switch (current) {
      case 0:
        return <Responsavel current={current} />;
      case 1:
        return <Student current={current} />
      case 2:
        return <Disciplinas current={current} disabled={isMatriculado} />
      case 3:
        return <Produtos current={current} disabled={isMatriculado} />
      case 4:
        return <Pagamento current={current} disabled={isMatriculado} />
    }
  };

  const handleBackToList = () => {
    navigate('/alunos');
  };

  return (
    <>
      <div className="mb-8 flex items-center justify-between">
        <span className="text-white text-2xl">Cadastrar aluno</span>
      </div>

      <ConfigProvider
        theme={{
          algorithm: [theme.compactAlgorithm, theme.darkAlgorithm],
        }}
      >
        <Row
          gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
        >
          {/*
            Coluna lateral fixa: steps e resumo ficam parados enquanto o
            formulário do step rola. `sticky` prende no topo do container de
            rolagem do MainContent; quando o conteúdo passa da altura da tela,
            esta coluna ganha a própria barra de rolagem, separada da principal.
          */}
          <Col span={6} style={{ minWidth: '207px' }}>
            <div
              className="sticky top-0 overflow-y-auto pr-2"
              style={{ maxHeight: 'calc(100vh - 8rem)' }}
            >
              <Steps
                current={current}
                onChange={onChange}
                direction="vertical"
                items={mountSteps()}
              />

              <ResumoPagamento
                valorTotal={calcularTotalMatricula(watch('disciplinas'), watch('produtos'))}
              />
            </div>
          </Col>

          <Col span={18}>
            <FormProvider {...methods}>
              <form onSubmit={onSubmit}>
                {renderForm()}
                <MultiFormNavigation
                  current={current}
                  steps={steps}
                  next={next}
                  prev={prev}
                  editable={isEditable}
                  onCancel={handleBackToList}
                  cancelText="Cancelar"
                />
              </form>
            </FormProvider>
          </Col>
        </Row>
      </ConfigProvider>
    </>
  )
}

export default CadastroAluno;